import ELK from 'elkjs/lib/elk.bundled.js'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import {
    ReactFlow,
    addEdge,
    Panel,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Controls,
    MiniMap,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '../../App.css'
import DepartmentNode from './departmentNode'
import PositionNode from './positionNode'
import CustomEdge from './CustomEdge'
import RequestQueue from '../../api/requestQueue'
import { toast } from 'react-toastify'
import Loading from '../../component/loading'
import ModalAddDepartment from '../../component/modals/modalAddDepartment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowsRotate,
    faBuilding,
    faFloppyDisk,
    faPlus,
} from '@fortawesome/free-solid-svg-icons'
import ModalEditDepartment from '../../component/modals/modalEditDepartment'

const nodeTypes = {
    DepartmentNode,
    PositionNode,
}

const edgeTypes = {
    'custom-edge': CustomEdge,
}

const elk = new ELK()
const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.layered.spacing.nodeNodeBetweenLayers': '100',
    'elk.spacing.nodeNode': '80',
}

const convertDataToNodesAndEdges = (
    departments,
    handlerAddDepartment,
    handleIcon2Click,
    onClickChildDepartment2,
    handlerEditDepartmentId,
    handlerDeleteDepartmentId
) => {
    const nodes = []
    const edges = []

    // ایجاد نودها برای بخش کاری‌ها
    departments.forEach((department) => {
        nodes.push({
            id: department.id,
            data: {
                label: department.name,
                handlerAddDepartment: () =>
                    handlerAddDepartment(department.id, department.parent_id),
                onClickIcon2: () =>
                    handleIcon2Click(department.id, department.parent_id),
                onClickChildDepartment: () => {
                    return onClickChildDepartment2(department)
                },
                handlerEditDepartment: () => {
                    return handlerEditDepartmentId(department)
                },
                handlerDeleteDepartment: () => {
                    return handlerDeleteDepartmentId(department)
                },
            },
            type: 'DepartmentNode', // تغییر نوع به 'department'
            position: { x: 0, y: 0 },
        })
    })

    // ایجاد لبه‌ها برای ارتباطات بین بخش کاری‌ها
    departments.forEach((department) => {
        if (department.parent_id) {
            edges.push({
                id: `e${department.parent_id}-${department.id}`,
                source: department.parent_id,
                target: department.id,
                data: {
                    id: department.id,
                    name: department.name,
                    parent_id: department.parent_id,
                },
                type: 'custom-edge',
            })
        }
    })

    return { nodes, edges }
}

const getLayoutedElements = (nodes, edges, options = {}) => {
    const isHorizontal = options?.['elk.direction'] === 'RIGHT'

    const graph = {
        id: 'root',
        layoutOptions: options,
        children: nodes.map((node) => ({
            ...node,
            targetPosition: isHorizontal ? 'left' : 'top',
            sourcePosition: isHorizontal ? 'right' : 'bottom',
            width:
                node.data.label.length * 15 > 100
                    ? node.data.label.length * 15 + 15
                    : 115, // تنظیم عرض بر اساس طول برچسب
            height: 60, // تنظیم ارتفاع به صورت ثابت
        })),
        edges: edges,
    }

    return elk
        .layout(graph)
        .then((layoutedGraph) => ({
            nodes: layoutedGraph.children.map((node) => ({
                ...node,
                position: { x: node.x, y: node.y },
            })),
            edges: layoutedGraph.edges,
        }))
        .catch(console.error)
}

const findNonTargetedNodes = (nodes, edges) => {
    const nodeIds = nodes.map((node) => node.id)
    const targetIds = edges.map((edge) => edge.target)

    const nonTargetedNodeIds = nodeIds.filter((id) => !targetIds.includes(id))

    return nonTargetedNodeIds
}

function Organization3Layout({ setSelectedDepartment }) {
    const [nodes, setNodes, onNodesChange] = useNodesState([])
    const [edges, setEdges, onEdgesChange] = useEdgesState([])
    const { fitView } = useReactFlow()
    const [loading, setLoading] = useState(false)

    const [departmentData, setDepartmentData] = useState([])
    const [updatedDepartmentData, setUpdatedDepartmentData] = useState([])
    const [positionData, setPositionData] = useState([])
    const [positionDataSelected, setPositionDataSelected] = useState(null)

    const [organizations, setOrganizations] = useState([])
    const [selectOrganization, setSelectOrganization] = useState(null)

    useEffect(() => {
        setSelectOrganization(
            RequestQueue.getAuthResult()?.user?.organization_id
        )
        if (RequestQueue.getAuthResult()?.user?.role?.name === 'superAdmin') {
            handlerGetOrganizations()
        }
    }, [])

    useEffect(() => {
        getDepartment()
    }, [selectOrganization])

    const handlerGetOrganizations = async () => {
        setLoading(true)
        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/organization`,
            },
            resolve: (res) => {
                setLoading(false)
                setOrganizations(res.data)
                setSelectOrganization(res.data[0]?.id)
                getDepartment(res.data[0]?.id)
            },
            reject: (error) => {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور', {
                    position: 'bottom-center',
                })
            },
        })
    }

    const getDepartment = async (selectOrganizationId) => {
        setLoading(true)
        let organizations_id =
            RequestQueue.getAuthResult()?.user?.role?.name === 'superAdmin'
                ? selectOrganization
                : RequestQueue.getAuthResult()?.user?.organization_id

        if (selectOrganizationId) {
            organizations_id = selectOrganizationId
        }
        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/department/${organizations_id}`,
            },
            resolve: (res) => {
                setLoading(false)
                setDepartmentData(res.data)
                setUpdatedDepartmentData(res.data)
            },
            reject: (error) => {
                setLoading(false)
            },
        })
    }

    useEffect(() => {
        setSelectedDepartment(positionDataSelected)
    }, [positionDataSelected])

    useEffect(() => {
        getPosition()
    }, [])

    const getPosition = async () => {
        setLoading(true)

        await RequestQueue.sendRequest({
            config: {
                method: 'get',
                url: `/position`,
            },
            resolve: (res) => {
                setPositionData(res.data)
                setLoading(false)
            },
            reject: (error) => {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور', {
                    position: 'bottom-center',
                })
            },
        })
    }

    const handlerAddDepartment = (id, parentId) => {
        setIdAddDepartment(id)
        setShowModalAddDepartment(true)
        console.log(
            `Icon 1 clicked for department ${id} with parent ${parentId}`
        )
        // Add your custom logic here
    }

    const handleIcon2Click = (id, parentId) => {
        console.log(
            `Icon 2 clicked for department ${id} with parent ${parentId}`
        )
        // Add your custom logic here
    }

    const onClickChildDepartment2 = (data) => {
        setPositionDataSelected(data)
    }

    const onConnect = useCallback(
        (connection) => {
            let edge = { ...connection, type: 'custom-edge' }
            const idConnection = edge.target
            const parentConnection = edge.source

            const searchDepartment = departmentData.find(
                (dep) => dep.id === idConnection
            )

            let nameConnection = ''
            if (searchDepartment) {
                nameConnection = searchDepartment.name
            }
            edge.data = {
                id: idConnection,
                name: nameConnection,
                parent_id: parentConnection,
            }

            setEdges((eds) => addEdge(edge, eds))

            // Update department data
            const updatedData = updatedDepartmentData.map((dep) => {
                if (dep.id === idConnection) {
                    return { ...dep, parent_id: parentConnection }
                }
                return dep
            })
            setUpdatedDepartmentData(updatedData)
        },
        [setEdges, departmentData, updatedDepartmentData]
    )

    const onLayout = useCallback(
        ({ direction }) => {
            const opts = { 'elk.direction': direction, ...elkOptions }
            const { nodes: initialNodes, edges: initialEdges } =
                convertDataToNodesAndEdges(
                    departmentData,
                    handlerAddDepartment,
                    handleIcon2Click,
                    onClickChildDepartment2,
                    handlerEditDepartmentId,
                    handlerDeleteDepartmentId
                )

            getLayoutedElements(initialNodes, initialEdges, opts).then(
                ({ nodes: layoutedNodes, edges: layoutedEdges }) => {
                    setNodes(layoutedNodes)
                    setEdges(layoutedEdges)
                    window.requestAnimationFrame(() => fitView())
                }
            )
        },
        [setNodes, setEdges, fitView, departmentData]
    )

    useEffect(() => {
        const nonTargetedNodeIds = findNonTargetedNodes(nodes, edges)
        console.log('Non-targeted Node IDs:', nonTargetedNodeIds)

        const updatedData = updatedDepartmentData.map((dep) => {
            if (nonTargetedNodeIds.includes(dep.id)) {
                return { ...dep, parent_id: null }
            }
            return dep
        })

        setUpdatedDepartmentData(updatedData)
    }, [edges])

    useLayoutEffect(() => {
        onLayout({ direction: 'DOWN' })
    }, [onLayout])

    const [idAddDepartment, setIdAddDepartment] = useState('')
    const [showModalAddDepartment, setShowModalAddDepartment] = useState(false)
    const [showModalEditDepartment, setShowModalEditDepartment] =
        useState(false)
    const openModalAddDepartment = (id) => {
        setIdAddDepartment(id)
        setShowModalAddDepartment((prev) => !prev)
    }

    const [selectedDepartmentID, setSelectedDepartmentID] = useState(null)
    const handlerEditDepartmentId = (d) => {
        setSelectedDepartmentID(d.id)
        setShowModalEditDepartment(true)
    }

    const handlerDeleteDepartmentId = async (d) => {
        setLoading(true)

        await RequestQueue.sendRequest({
            config: {
                method: 'delete',
                url: `/department/${d.id}`,
            },
            resolve: (res) => {
                setLoading(false)
                getDepartment()
                getPosition()
            },
            reject: (error) => {
                setLoading(false)
            },
        })
    }

    const updateConnectionDepartment = async () => {
        const changes = updatedDepartmentData.filter((updatedDep) => {
            const originalDep = departmentData.find(
                (dep) => dep.id === updatedDep.id
            )
            return originalDep && originalDep.parent_id !== updatedDep.parent_id
        })
        console.log(changes, 'changes')
        console.log(departmentData, 'departmentData1')
        console.log(updatedDepartmentData, 'updatedDepartmentData1')
        for (const change of changes) {
            try {
                setLoading(true)
                await RequestQueue.sendRequest({
                    config: {
                        method: 'patch',
                        url: `/department/${change.id}`,
                        data: {
                            parent_id: change.parent_id,
                            name: change.name,
                        },
                    },
                    resolve: (res) => {
                        setLoading(false)
                    },
                    reject: (error) => {},
                })
            } catch (error) {
                setLoading(false)
                toast.dismiss()
                toast.error('خطا در ارتباط با سرور', {
                    position: 'bottom-center',
                })
            }
        }

        // Refresh department data after updates
        getDepartment()
    }
    console.log(updatedDepartmentData, 'updatedDepartmentData')

    return (
        <>
            {loading ? <Loading /> : null}
            {showModalAddDepartment ? (
                <ModalAddDepartment
                    showModal={showModalAddDepartment}
                    setShowModal={setShowModalAddDepartment}
                    selectOrganization={selectOrganization}
                    handlerSubmit={() => {}}
                    loadData={() => {
                        getDepartment()
                        getPosition()
                    }}
                    id={idAddDepartment}
                />
            ) : null}
            {showModalEditDepartment ? (
                <ModalEditDepartment
                    showModal={showModalEditDepartment}
                    setShowModal={setShowModalEditDepartment}
                    selectOrganization={selectOrganization}
                    handlerSubmit={() => {}}
                    loadData={() => {
                        getDepartment()
                        getPosition()
                    }}
                    id={selectedDepartmentID}
                />
            ) : null}

            <div className="flex w-full justify-between items-end mt-2 maxLg:flex-col">
                {RequestQueue.getAuthResult()?.user?.role?.name ===
                    'superAdmin' && (
                    <div className="space-y-2">
                        <label className=" text-sm font-medium text-gray-700 flex items-center">
                            <FontAwesomeIcon
                                icon={faBuilding}
                                className="ml-2"
                            />
                            سازمان
                        </label>
                        <select
                            className="w-full px-4 py-3 border bg-white border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            onChange={(e) => {
                                setSelectOrganization(e.target.value)
                            }}
                        >
                            {organizations.map((d) => {
                                return (
                                    <option
                                        value={d.id}
                                        selected={d.id === selectOrganization}
                                    >
                                        {d.name}
                                    </option>
                                )
                            })}
                        </select>
                    </div>
                )}
                <button
                    className="p-4 m-1 rounded-md green-clr-bg white-clr flex items-center"
                    onClick={updateConnectionDepartment}
                >
                    <FontAwesomeIcon icon={faFloppyDisk} className="ml-2" />
                    ذخیره
                    <div className="w-2 h-2 mr-2 rounded-full bg-white animate-ping"></div>
                </button>
                <button
                    className="p-4  m-1  rounded-md bg-indigo-500 white-clr"
                    onClick={() => {
                        setIdAddDepartment(null)
                        setShowModalAddDepartment(true)
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} className="ml-2" />
                    افزودن بخش کاری
                </button>
            </div>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                onConnect={onConnect}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                fitView
                id="flowTree"
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                style={{ width: '100%', height: '100vh' }}
            >
                <Panel position="top-right">
                    <button
                        className="axis-flow-btn"
                        onClick={() => onLayout({ direction: 'DOWN' })}
                    >
                        نمایش عمودی
                    </button>
                    <button
                        className="axis-flow-btn"
                        onClick={() => onLayout({ direction: 'RIGHT' })}
                    >
                        نمایش افقی
                    </button>
                </Panel>
                <MiniMap />
                <Controls />
            </ReactFlow>
        </>
    )
}

export default Organization3Layout
